// azure-storage-operations.ts
import { Observable, from, of, throwError } from "rxjs";
import { map, catchError, tap, switchMap } from "rxjs/operators";
import { IStorageOperations } from "./storage-operations.interface";
import { StorageDataTransferService } from "src/app/core/services/storage-data-transfer.service";
import {
  StorageItem,
  UploadProgress,
  OperationResult,
  FileOperationContext,
  DownloadResult,
  UploadContext
} from "src/app/core/models/storage-models.interface";
import { StorageDataType } from "src/app/core/models/storage-data-type";
import { ManageStorageLogsService } from "src/app/core/services/manage-storage-logs.service";
import { EnvironmentService } from "src/app/core/services/environment.service";

export class AzureStorageOperations implements IStorageOperations {

  constructor(
    private azureService: StorageDataTransferService,
    private storageLogService: ManageStorageLogsService,
    private env : EnvironmentService,
    private isBlob: boolean = false,
  ) { }

  listFiles(path: string, context: FileOperationContext, uniqueId?: number, cancelContext?: any): Observable<StorageItem[]> {
    if(path === this.env.envVariables.syncStoreRoute && !this.isBlob){
      path = ".";
    }
    if (this.isBlob) {
      return this.azureService.listBlobs(path).pipe(
        map((data: StorageDataType[]) => this.mapToStorageItems(data)),
        catchError(error => throwError(() => new Error(`Failed to list blobs: ${error.message}`)))
      );
    } else {
      return from(this.azureService.azureFilesListFiles(path, uniqueId, cancelContext)).pipe(
        map((data: StorageDataType[]) => this.mapToStorageItems(data)),
        catchError(error => throwError(() => new Error(`Failed to list files: ${error.message}`)))
      );
    }
  }

  uploadFile(
    file: File,
    destinationPath: string,
    context: FileOperationContext,
    uniqueId: number,
    uploadContext?: UploadContext
  ): Observable<UploadProgress> {
    return new Observable<UploadProgress>(observer => {
      if (this.isBlob) {
        const upload$ = this.azureService.uploadBlob(destinationPath, file, uniqueId);

        upload$.subscribe({
          next: () => {
            observer.next({ loaded: file.size, total: file.size, percentage: 100 });
            observer.complete();
          },
          error: (error) => observer.error(new Error(`Blob upload failed: ${error.message}`))
        });
      } else {
        const processedPath = destinationPath === this.env.envVariables.syncStoreRoute ? "." : destinationPath;
        
        const directoryPath = processedPath.replace(this.env.envVariables.syncStoreRoute + '/', '');
        const pathsToVerify = this.buildDirectoryPaths(directoryPath);
        
        from(this.azureService.verifyDirectoryPath(pathsToVerify))
          .pipe(
            switchMap(() => {
              return from(this.azureService.azureFilesUploadFile(
                processedPath,
                file,
                uniqueId,
                uploadContext?.overwriteExisting
              ));
            })
          )
          .subscribe({
            next: () => {
              observer.next({ loaded: file.size, total: file.size, percentage: 100 });
              observer.complete();
            },
            error: (error) => {
              observer.error(new Error(`Azure Files upload failed: ${error.message || error}`));
            }
          });
      }
    });
  }

  private buildDirectoryPaths(directoryPath: string): string[] {
    if (!directoryPath || directoryPath === '.') {
      return [];
    }
    
    const paths: string[] = [];
    const parts = directoryPath.split('/').filter(part => part.length > 0);
    
    // Build incremental paths: ['user', 'user/folder', 'user/folder/subfolder']
    for (let i = 0; i < parts.length; i++) {
      paths.push(parts.slice(0, i + 1).join('/'));
    }
    
    return paths;
  }

  downloadFile(filePath: string, context: FileOperationContext, uniqueId?: number): Observable<DownloadResult> {
    const fileName = filePath.split('/').pop() || '';

    if (uniqueId) {
      this.storageLogService.updateStatusOfLog(uniqueId, `Download started for ${fileName}`);
    }

    if (this.isBlob) {
      return this.azureService.downloadBlob(filePath).pipe(
        map(url => ({
          url: url,
          directDownload: false,
          fileName: fileName
        })),
        tap(() => {
          if (uniqueId) {
            this.storageLogService.updateRequestCompleteFields(uniqueId, "Download Completed");
          }
        }),
        catchError(error => {
          if (uniqueId) {
            this.storageLogService.updateRequestCompleteFields(uniqueId, "Download Failed");
          }
          return throwError(() => new Error(`Azure Blob download failed: ${error.message}`));
        })
      );
    } else {
      const dirPath = filePath.substring(0, filePath.lastIndexOf('/'));
      
      return this.azureService.azureFilesDownloadFile(fileName, dirPath, 0, uniqueId || Date.now()).pipe(
        map(blob => {
          if (blob === null) {
            return {
              blob: null,
              directDownload: true,
              fileName: fileName,
              cancelled: true
            } as DownloadResult & { cancelled?: boolean };
          }
          return {
            blob: blob,
            directDownload: true,
            fileName: fileName
          };
        }),
        catchError(error => {
          return throwError(() => new Error(`Azure Files download failed: ${error.message}`));
        })
      );
    }
  }

  cancelOperation(uniqueId: number, operationType: 'upload' | 'download' | 'zip' | 'unzip' | 'general'): boolean {
    
    switch (operationType) {
      case 'upload':
        this.storageLogService.cancelAzureUploadRequest(uniqueId);
        return true;
        
      case 'download':
        this.storageLogService.cancelAzureDownloadRequest(uniqueId);
        return true;
        
      case 'zip':
      case 'unzip':
        this.storageLogService.updateRequestCompleteFields(uniqueId, `${operationType} not supported`);
        return false;
        
      default:
        this.storageLogService.updateRequestCompleteFields(uniqueId, "Operation Cancelled");
        return true;
    }
  }

  deleteFile(filePath: string, context: FileOperationContext, uniqueId?: number): Observable<OperationResult> {
    const fileName = filePath.split('/').pop() || '';
    const isDirectory = !fileName.includes('.');
    
    if (this.isBlob) {
      // Azure Blob Storage deletion
      const blobPath = isDirectory ? `${filePath}/` : filePath;
      return this.azureService.deleteBlob(blobPath).pipe(
        map(() => ({ success: true, message: 'File deleted successfully' })),
        catchError(error => throwError(() => new Error(`Azure Blob delete failed: ${error.message}`)))
      );
    } else {
      // Azure Files deletion
      const dirPath = filePath.substring(0, filePath.lastIndexOf('/'));
      const processedDirPath = dirPath === context.syncStorePath ? '.' : dirPath;
      
      if (isDirectory) {
        return from(this.azureService.azureRecursiveFolderDelete(fileName, processedDirPath, uniqueId || Date.now())).pipe(
          map(() => ({ success: true, message: 'Directory deleted successfully' })),
          catchError(error => throwError(() => new Error(`Azure Files directory delete failed: ${error.message}`)))
        );
      } else {
        return from(this.azureService.azureFilesDeleteFile(fileName, processedDirPath, uniqueId || Date.now())).pipe(
          map(() => ({ success: true, message: 'File deleted successfully' })),
          catchError(error => throwError(() => new Error(`Azure Files delete failed: ${error.message}`)))
        );
      }
    }
  }

  createDirectory(directoryName: string, path: string, context: FileOperationContext, uniqueId?: number): Observable<OperationResult> {
    let processedPath = path;
    
    if (processedPath === this.env.envVariables.syncStoreRoute) {
      processedPath = ".";
    }
    
    if (this.isBlob) {
      let dirPathForBlob = path + "/" + directoryName + "/";
      const emptyFile = new File([''], directoryName + '/');
      
      return this.azureService.uploadBlob(dirPathForBlob, emptyFile, uniqueId || Date.now()).pipe(
        map(() => ({ success: true, message: 'Blob directory created successfully' })),
        tap(() => {
          if (uniqueId) {
            this.storageLogService.updateTimeRemaining(uniqueId, null);
            this.storageLogService.updateRequestCompleteFields(uniqueId, "Completed");
          }
        }),
        catchError(error => {
          if (uniqueId) {
            this.storageLogService.updateTimeRemaining(uniqueId, null);
            this.storageLogService.updateRequestCompleteFields(uniqueId, "Failed");
          }
          return throwError(() => new Error(`Azure Blob directory creation failed: ${error.message}`));
        })
      );
    } else {
      // For Azure Files
      return from(this.azureService.azureFilesCreateDirectory(directoryName, processedPath, uniqueId || Date.now())).pipe(
        map(() => ({ success: true, message: 'Azure Files directory created successfully' })),
        catchError(error => throwError(() => new Error(`Azure Files directory creation failed: ${error.message}`)))
      );
    }
  }

  copyFile(sourcePath: string, destinationPath: string, context: FileOperationContext): Observable<OperationResult> {
    if (this.isBlob) {
      return this.azureService.copyBlob(sourcePath, destinationPath).pipe(
        map(() => ({ success: true, message: 'Blob copied successfully' })),
        catchError(error => throwError(() => new Error(`Blob copy failed: ${error.message}`)))
      );
    } else {
      // Azure Files doesn't have native copy
      return throwError(() => new Error('Copy operation not supported for Azure Files'));
    }
  }

  renameFile(oldPath: string, newName: string, context: FileOperationContext): Observable<OperationResult> {
    if (this.isBlob) {
      const newPath = oldPath.substring(0, oldPath.lastIndexOf('/')) + '/' + newName;
      return this.azureService.renameBlob(oldPath, newPath).pipe(
        map(() => ({ success: true, message: 'Blob renamed successfully' })),
        catchError(error => throwError(() => new Error(`Blob rename failed: ${error.message}`)))
      );
    } else {
      const operationId = Date.now();
      return from(this.azureService.azureFilesRename(oldPath, newName, operationId)).pipe(
        map(() => ({ success: true, message: 'File renamed successfully' })),
        catchError(error => throwError(() => new Error(`File rename failed: ${error.message}`)))
      );
    }
  }

  checkFileExists(filePath: string, context: FileOperationContext): Observable<boolean> {
    if (this.isBlob) {
      return this.listFiles(filePath.substring(0, filePath.lastIndexOf('/')), context).pipe(
        map(files => {
          const fileName = filePath.split('/').pop();
          return files.some(file => file.name === fileName);
        }),
        catchError(() => throwError(() => false))
      );
    } else {
      const fileName = filePath.split('/').pop() || '';
      const dirPath = filePath.substring(0, filePath.lastIndexOf('/'));
      return from(this.azureService.azureFilesExists(dirPath, fileName)).pipe(
        map(exists => !!exists),
        catchError(() => throwError(() => false))
      );
    }
  }

  generateDownloadUrl(filePath: string, context: FileOperationContext): Observable<string> {
    if (this.isBlob) {
      const fileName = filePath.split('/').pop() || '';
      const fullPath = context.currentPath + "/" + fileName;
      
      return this.azureService.downloadBlob(fullPath).pipe(
        map(url => url as string),
        catchError(error => throwError(() => new Error(`Failed to generate Azure Blob download URL: ${error.message}`)))
      );
    } else {
      return throwError(() => new Error('Download URL generation not supported for Azure Files'));
    }
  }

  readFileContent(filePath: string, context: FileOperationContext): Observable<string> {
    if (this.isBlob) {
      return this.azureService.viewBlob(filePath).pipe(
        map((result: any) => {
          if (typeof result === 'string') {
            return result;
          } else if (result instanceof Blob) {
            return 'Binary content not supported for editing';
          }
          return result.toString();
        }),
        catchError(error => throwError(() => new Error(`Failed to read blob content: ${error.message}`)))
      );
    } else {
      const fileName = filePath.split('/').pop() || '';
      const dirPath = filePath.substring(0, filePath.lastIndexOf('/'));
      return this.azureService.getFileContent(dirPath, fileName).pipe(
        catchError(error => throwError(() => new Error(`Failed to read file content: ${error.message}`)))
      );
    }
  }

  saveFileContent(filePath: string, content: string | File, context: FileOperationContext): Observable<OperationResult> {
    if (content instanceof File) {
      return this.uploadFile(content, filePath, context, Date.now()).pipe(
        map(() => ({ success: true, message: 'File saved successfully' }))
      );
    } else {
      const fileName = filePath.split('/').pop() || 'file.txt';
      const file = new File([content], fileName, { type: 'text/plain' });

      return this.uploadFile(file, filePath, context, Date.now()).pipe(
        map(() => ({ success: true, message: 'File content saved successfully' }))
      );
    }
  }

  private mapToStorageItems(storageData: StorageDataType[]): StorageItem[] {
    return storageData.map(item => ({
      name: item.name,
      type: item.type === 'directory' ? 'directory' as const : 'file' as const,
      path: item.path,
      owner: item.owner,
      date: item.date ? new Date(item.date) : undefined,
      size: item.size,
      lastModified: item.date ? new Date(item.date) : undefined
    }));
  }

  verifyDirectoryPath(paths: string[], context: FileOperationContext): Observable<boolean> {
    if (this.isBlob) {
      return of(true);
    } else {
      return from(this.azureService.verifyDirectoryPath(paths));
    }
  }

  viewFileData(filePath: string, context: FileOperationContext, uniqueId?: number): Observable<any> {
    if (this.isBlob) {
      return this.azureService.viewBlob(filePath).pipe(
        catchError(error => throwError(() => new Error(`Failed to view Azure Blob: ${error.message}`)))
      );
    } else {
      const fileName = filePath.split('/').pop() || '';
      const dirPath = filePath.substring(0, filePath.lastIndexOf('/'));
      return this.azureService.getFileContent(dirPath, fileName).pipe(
        catchError(error => throwError(() => new Error(`Failed to view Azure File: ${error.message}`)))
      );
    }
  }
}
